import './PersonalData.scss';

import React, { useState } from 'react';
import { ID } from '../../fields/ID/ID';
import { Name } from '../../fields/Name/Name';
import { LastName } from '../../fields/LastName/LastName';
import { SecondLastName } from '../../fields/SecondLastName/SecondLastName';
import { BirthDate } from '../../fields/BirthDate/BirthDate';
import { Resident } from '../../fields/Resident/Resident';
import { Section } from '../../Section/Section';
import { FormattedText, formatText } from 'core/i18n';
import { mustShowInfoAnotherAccount, mustShowTreatmentFields } from 'utilities/logicByCountry';
import JointHolderContainer from '../../jointHolderContainer';
import ResidentContainer from '../../residentContainer';
import OlderContainer from '../../olderContainer';
import { doTagButtonUnderstood } from '../../tagging';
import { PUBLICA_URL } from 'constants/index';
import { Nationality } from '../../fields/Nationality/Nationality';
import { isCountrySpain } from 'utilities/multiLanguageHelper';
import { SECTION_TYPE } from '../../constants';
import { Treatment } from 'components/newAccountSteps/initialStep/fields/Treatment/Treatment';
import { InputPassport } from 'components/shared/InputPassport/InputPassport';
import { usePassport } from 'components/newAccountSteps/initialStep/sections/PersonalData/hooks/usePassport';
import { getIsSelectedCountryDifferent } from 'components/newAccountSteps/initialStep/sections/PersonalData/utils/getIsSelectedCountryDifferent';
import {
    doTagBirthDay,
    doTagBirthMonth,
    doTagBirthYear,
    doTagID,
    doTagIsResident,
    doTagLastName,
    doTagName,
    doTagNationalityClose,
    doTagNationalityOpen,
    doTagSecondLastName,
    doTagTin,
} from './tagging';

import { useNationalityOnChange } from './hooks/useNationalityOnChange';
import { usePersonalDataValidations } from './hooks/usePersonalDataValidations';
import { KeepInMindMessage } from './KeepInMindMessage/KeepInMindMessage';
import VulnerablyOlderAdvise from './VulnerablyOlderAdvise/VulnerablyOlderAdvise';

export const PersonalData = ({
    userProfile,
    formActions,
    userProfileForm,
    actions,
    focus,
    options,
    optionsActions,
    promoCode,
    isCollapsed,
    isValidated,
    isNewRegisterActive,
    handleModifySection,
    handleValidateSection,
    handleAdviseShown,
}) => {
    const { documentType, nationality, isValidatingTin } = userProfile;
    const isPassportRequired = usePassport({
        countries: options.get('countries'),
        nationality,
        documentType,
        isNewRegisterActive,
        formActions,
    });

    const [isResident, setIsResident] = useState(false);
    const [showLastNameDisclaimer, setShowLastNameDisclaimer] = useState(false);

    const {
        onValidateSection,
        handleValidateBirthDate,
        handleSecondLastNameValidation,
        handleValidateResident,
        isFormWithErrors,
        isValidDate,
        showResidentAdvise,
        showTooYoungAdvise,
        showJointHolderDocAdvise,
        shownIsVulnerablyOlder,
    } = usePersonalDataValidations({
        userProfile,
        userProfileForm,
        formActions,
        isValidatingTin,
        isPassportRequired,
        isResident,
        handleValidateSection,
        handleAdviseShown,
    });

    const { handleChangeNationality } = useNationalityOnChange({ userProfile, formActions });

    const onHandleClickRedirects = () => {
        doTagButtonUnderstood();
        window.location.href = PUBLICA_URL;
    };

    const handleOnChangeResident = value => {
        const isValueTruthy = value === 'true';
        setIsResident(isValueTruthy);
        doTagIsResident(isValueTruthy);
    };

    const shouldHideResidentAdvise = !showResidentAdvise || shownIsVulnerablyOlder;

    return (
        <Section
            isButtonDisabled={isFormWithErrors}
            hideFooter={showResidentAdvise || showTooYoungAdvise || showJointHolderDocAdvise || shownIsVulnerablyOlder}
            type={SECTION_TYPE.PERSONAL_DATA}
            isCollapsed={isCollapsed}
            isValidated={isValidated}
            handleModifySection={handleModifySection}
            handleValidateSection={onValidateSection}
            userProfile={userProfile}
            promoCode={promoCode}
        >
            <Nationality
                options={options}
                optionsActions={optionsActions}
                onChange={handleChangeNationality}
                onOpen={doTagNationalityOpen}
                onClose={doTagNationalityClose}
            />
            <div className="document-id-with-advise">
                <ID
                    userProfile={userProfile}
                    focus={focus}
                    userProfileForm={userProfileForm}
                    formActions={formActions}
                    validateSecondLastName={handleSecondLastNameValidation}
                    disabled={!nationality}
                    isSelectorDisabled={isCountrySpain() || !nationality || getIsSelectedCountryDifferent(nationality)}
                    onFocus={doTagID}
                />
                <KeepInMindMessage
                    nationality={nationality}
                    documentType={documentType}
                    isCountrySelectedFromEuropeanUnion={optionsActions.isCountrySelectedFromEuropeanUnion()}
                />
            </div>
            {isPassportRequired && (
                <div className="row">
                    <div className="col-sm-6">
                        <InputPassport
                            label={formatText('field-passport')}
                            userProfile={userProfile}
                            userProfileForm={userProfileForm}
                            formActions={formActions}
                            isRequired={isPassportRequired}
                        />
                    </div>
                </div>
            )}
            <Treatment
                userProfile={userProfile}
                userProfileForm={userProfileForm}
                formActions={formActions}
                focus={focus}
                hidden={!mustShowTreatmentFields()}
            />
            <Name
                formActions={formActions}
                userProfile={userProfile}
                userProfileForm={userProfileForm}
                onFocus={doTagName}
            />
            <LastName
                formActions={formActions}
                userProfile={userProfile}
                userProfileForm={userProfileForm}
                showDisclaimer={showLastNameDisclaimer}
                setShowDisclaimer={setShowLastNameDisclaimer}
                onFocus={doTagLastName}
            />
            <SecondLastName
                userProfile={userProfile}
                userProfileForm={userProfileForm}
                formActions={formActions}
                onFocus={doTagSecondLastName}
            />
            <div className={showJointHolderDocAdvise ? 'hidden' : ''}>
                <BirthDate
                    userProfile={userProfile}
                    userProfileForm={userProfileForm}
                    formActions={formActions}
                    isValidDate={isValidDate}
                    validateGroups={handleValidateBirthDate}
                    onFocusBirthDay={doTagBirthDay}
                    onFocusBirthMonth={doTagBirthMonth}
                    onFocusBirthYear={doTagBirthYear}
                />
                <div className={showTooYoungAdvise || shownIsVulnerablyOlder ? 'hidden' : ''}>
                    <Resident
                        userProfile={userProfile}
                        userProfileForm={userProfileForm}
                        formActions={formActions}
                        actions={actions}
                        handleValidateResident={handleValidateResident}
                        onChange={handleOnChangeResident}
                        onFocusTin={doTagTin}
                    />
                </div>
            </div>
            {mustShowInfoAnotherAccount() && (
                <FormattedText value="field-infoAnotherAccount" className="info-another-account" />
            )}
            <ResidentContainer
                hiddenResidentContainer={shouldHideResidentAdvise}
                handleOnClick={onHandleClickRedirects}
                id="countryRestriction"
            />
            <VulnerablyOlderAdvise shownIsVulnerablyOlder={shownIsVulnerablyOlder} onClick={onHandleClickRedirects} />
            <OlderContainer
                hiddenOlderContainer={!showTooYoungAdvise}
                handleOnClick={onHandleClickRedirects}
                id="ageRestriction"
            />
            <JointHolderContainer
                hiddenJointHolderContainer={!showJointHolderDocAdvise}
                handleOnClick={onHandleClickRedirects}
                id="JointHolderContainer"
            />
        </Section>
    );
};
